import { getSessionToken } from './session.js';

/**
 * Best-effort product analytics. Events are batched and POSTed to the BFF, which
 * injects identity from the session and forwards to PostHog. Non-load-bearing: on
 * any failure the batch is dropped (no retry) — analytics must never affect the
 * chat. Mirrors stream-chat's AnalyticsContext (batch + periodic flush + keepalive
 * on unload) as a module, matching chat-emp's `emitToHost` bridge style so it can
 * fire from non-React data code (`source.ts`) as well as components.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 10;

interface Queued {
  event: string;
  timestamp: string;
  properties?: Record<string, unknown>;
}

let queue: Queued[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let unloadBound = false;

export function track(event: string, properties?: Record<string, unknown>): void {
  queue.push({ event, timestamp: new Date().toISOString(), ...(properties && { properties }) });
  bindUnload();
  if (queue.length >= MAX_BATCH) flush();
  else if (!timer) timer = setTimeout(() => flush(), FLUSH_INTERVAL_MS);
}

function flush(keepalive = false): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const token = getSessionToken();
  // No session yet (or reauth in flight): drop the batch. Analytics isn't
  // load-bearing, and a raw fetch here deliberately does not trigger reauth.
  if (!token) {
    queue = [];
    return;
  }
  const events = queue;
  queue = [];
  fetch(`${API_BASE}/api/chat/analytics/track`, {
    method: 'POST',
    keepalive,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ events, viewMode: viewMode() }),
  }).catch(() => {});
}

function viewMode(): 'mobile' | 'desktop' {
  try {
    return window.matchMedia?.('(pointer: coarse)').matches ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
}

function bindUnload(): void {
  if (unloadBound || typeof window === 'undefined') return;
  unloadBound = true;
  // `pagehide` fires reliably when an iframe is torn down; `keepalive` lets the
  // final batch outlive the page.
  window.addEventListener('pagehide', () => flush(true));
}

/** Test seam: clear the pending batch and timer between cases. */
export function resetAnalytics(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  queue = [];
}
